<template>
  <mi-dialog
    class="restore-point-review-details-dialog"
    no-backdrop-dismiss
    :model-value="modelValue"
    :close-icon="false"
  >
    <div class="restore-point-review-details-dialog__header">
      <h3 class="q-ma-none">
        Review Details
      </h3>
      <mi-btn icon="close" flat @click="cancelChanges(false)"></mi-btn>
    </div>
    <mi-form @submit="confirmChanges">
      <mi-infinite-scroll>
        <div class="restore-point-review-details-dialog__content q-pt-md">
          <div class="restore-point-review-details-dialog__content-input">
            <mi-text-field
              v-model="restorePointName"
              :disable="!isUserAuthorized"
              :rules="[maxLength(35,'Max length of 35 characters reached'),required]"
              label="Name"
              placeholder="Enter a name"
              outlined
            ></mi-text-field>
          </div>
          <div class="restore-point-review-details-dialog__content-input">
            <mi-text-field
              v-model="restorePointDescription"
              :disable="!isUserAuthorized"
              :rules="[maxLength(80,'Max length of 80 characters reached')]"
              label="Description (optional)"
              placeholder="Enter a description"
              outlined
            ></mi-text-field>
          </div>
          <div class="restore-point-review-details-dialog__content-list">
            <p class="label">Review options included ({{ optionsAndChoicesSelectedList.length }})</p>
            <search-results-table
              ref="previewItemsTable"
              v-model="searchString"
              :header-color="optionsAndChoicesSelectedList.length === 0 ? noSelectedChoicesColor : worldColor"
              :items="filteredOptionsAndChoicesSelectedList"
              :show-no-data-found="!!searchString?.length
                && !filteredOptionsAndChoicesSelectedList.length"
              :pac-restore-point="true"
              placeholder="Filter for options or choices"
            >
              <template #default="{ item }">
                <search-results-group
                  :key="item.id2"
                  :item="item"
                  :color="worldColor"
                  :pac-restore-point="true"
                ></search-results-group>
              </template>
            </search-results-table>
          </div>
        </div>
      </mi-infinite-scroll>
      <div class="restore-point-review-details-dialog__footer">
        <div class="flex justify-end">
          <mi-btn class="q-mr-sm" flat @click="cancelChanges(false)"> Cancel </mi-btn>
          <mi-btn
            :disable="!isUserAuthorized"
            type="submit"
            :loading="isUpdateRestorePointLoading"
          >
            Confirm changes
          </mi-btn>
        </div>
      </div>
    </mi-form>
  </mi-dialog>
</template>

<script>
  import { computed, onBeforeMount, ref } from 'vue'
  import { useStore } from 'vuex'
  import MiBtn from '../../../../../packages/@mi-library/MiBtn/MiBtn.vue'
  import { getRestorePointData, showHTTPErrorNotification, updateRestorePoint } from '../../../../../api'
  import SearchResultsGroup from '../../../../search/search-results/SearchResultsGroup.vue'
  import SearchResultsTable from '../../../../search/search-results/SearchResultsTable.vue'
  import { ELEMENT_COLORS } from '../../../../../constants'
  import { maxLength, required } from '../../../../../utils/validators'
  import MiInfiniteScroll from '../../../../../packages/@mi-library/MiInfiniteScroll/MiInfiniteScroll.vue'

  export default {
    name: 'ConfigurationRestorePointReviewDetailsModal',
    components: { MiInfiniteScroll, SearchResultsTable, SearchResultsGroup, MiBtn },
    props: {
      modelValue: {
        type: Boolean,
        required: true
      },
      toggleModelValue: {
        type: Function,
        required: true
      },
      refreshRestorePoints: {
        type: Function,
        required: true
      },
      restorePointId: {
        type: String,
        required: true
      },
      configurationId: {
        type: String,
        required: true
      },
      isUserAuthorized: {
        type: Boolean,
        required: true
      }
    },
    setup(props) {
      const lastSavedRestorePointDetails = ref({})
      const restorePointName = ref('')
      const restorePointDescription = ref('')
      const optionsAndChoicesSelectedList = ref([])
      const worldColor = ref(ELEMENT_COLORS.YELLOW)
      const noSelectedChoicesColor = ref(ELEMENT_COLORS.DISABLED)
      const searchString = ref('')
      const store = useStore()
      const allOptions = computed(() => store.state.pac.options.items)
      const isUpdateRestorePointLoading = ref(false)

      const confirmChanges = async () => {
        try {
          isUpdateRestorePointLoading.value = true
          await updateRestorePoint(
            props.configurationId,
            props.restorePointId,
            {
              name: restorePointName.value,
              description: restorePointDescription.value
            }
          )
          props.toggleModelValue(false)
          props.refreshRestorePoints()
        }
        catch (e) {
          showHTTPErrorNotification({ status: e.status, message: e.message })
        }
        finally {
          isUpdateRestorePointLoading.value = false
        }
      }

      const doesFieldMatchSearch = (lowerSearch, field = '') => field.toLowerCase().includes(lowerSearch)

      const doesOptionMatchSearch = ({ id, id2, name }, lowerSearch) => [id, id2, name].some(
        field => doesFieldMatchSearch(
          lowerSearch, field
        )
      )

      const doesChoiceMatchSearch = (
        { id: choiceId, id2: choiceId2, name: choiceName }, lowerSearch
      ) => [choiceId, choiceId2, choiceName].some(field => doesFieldMatchSearch(
        lowerSearch, field
      ))

      const doesMappedOptionMatchSearch = ({ id, id2, name, choices = [] }, lowerSearch) => {
        const matchesOption = doesOptionMatchSearch({ id, id2, name }, lowerSearch)
        const matchesChoice = choices.some(choice => doesChoiceMatchSearch(
          choice, lowerSearch
        ))

        return matchesOption || matchesChoice
      }

      const filterMappedOptionsBySearch = searchStringValue => {
        const lowerSearch = searchStringValue.toLowerCase()

        return optionsAndChoicesSelectedList.value.filter(
          mappedOption => doesMappedOptionMatchSearch(mappedOption, lowerSearch)
        )
      }

      const filteredOptionsAndChoicesSelectedList = computed(
        () => filterMappedOptionsBySearch(searchString.value)
      )

      const formatOptionsAndChoicesSelectedList = selectedChoicesList => {
        const selectedChoiceId2Set = new Set(
          selectedChoicesList.map(({ choice }) => choice.id2)
        )

        optionsAndChoicesSelectedList.value = allOptions.value.reduce((optionsReduced, { id, id2, name, choices }) => {
          const filteredChoices = choices
            .filter(({ id2: choiceId2 }) => selectedChoiceId2Set.has(choiceId2))
            .map(({ id: choiceId, id2: choiceId2, name: choiceName }) => ({
              id: choiceId,
              id2: choiceId2,
              name: choiceName
            }))

          if (filteredChoices.length > 0) {
            optionsReduced.push({ id, id2, name, choices: filteredChoices })
          }

          return optionsReduced
        }, [])
      }

      const loadRestorePointDetails = async () => {
        try {
          const data = await getRestorePointData(
            props.configurationId,
            props.restorePointId,
            true
          )

          lastSavedRestorePointDetails.value = { name: data.name, description: data.description }
          formatOptionsAndChoicesSelectedList(data.configuration.selectedChoices)
          restorePointName.value = data.name || ''
          restorePointDescription.value = data.description || ''
        }
        catch (e) {
          showHTTPErrorNotification({ status: e.status, message: e.message })
        }
      }

      const cancelChanges = () => {
        restorePointName.value = lastSavedRestorePointDetails.value.name || ''
        restorePointDescription.value = lastSavedRestorePointDetails.value.description || ''
        props.toggleModelValue(false)
      }

      onBeforeMount(() => {
        loadRestorePointDetails()
      })

      return {
        restorePointName,
        restorePointDescription,
        loadRestorePointDetails,
        cancelChanges,
        lastSavedRestorePointDetails,
        optionsAndChoicesSelectedList,
        worldColor,
        searchString,
        filteredOptionsAndChoicesSelectedList,
        confirmChanges,
        isUpdateRestorePointLoading,
        noSelectedChoicesColor
      }
    },
    methods: { required, maxLength }
  }
</script>

<style lang="scss">
  .restore-point-review-details-dialog {
    .q-dialog__inner > .mi-card {
      width: 918px;
      max-width: 918px;
      min-width: 580px;
    }

    .mi-card > .mi-card__section {
      padding: 0;
    }

    .mi-infinite-scroll {
      overflow: auto;
      height: calc(100vh - 390px);
      overflow-y: scroll;
    }

    .mi-infinite-scroll::-webkit-scrollbar {
      width: .1em;
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 18px 24px;
      background-color: $mi-anthracite-50;
    }

    &__content {
      flex: 1;
      overflow-y: auto;
    }

    &__content-input {
      padding: 0 16px;
    }

    &__content-list {
      padding: 0 24px;

      .label {
        color: $mi-anthracite-800;
        font-family: $mi-typography-font-family-condensed;
        font-weight: 700;
        font-size: 14px;
        line-height: 14px;
        letter-spacing: 0;
      }
    }

    .search-results-table__scroll-area {
      height: calc(100vh - 530px);
    }

    &__footer {
      padding: 16px 24px;
    }
  }
</style>
